import { AlertTriangle, Plus } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'react-toastify'
import {
	useCreateKind,
	useDeleteKind,
	useKindList,
	useUpdateKind,
} from '@/api/queries/catalog-queries'
import { type Typology, TypologyFormContent } from '@/components/administration'
import {
	AdminCardPage,
	ResponsiveFormModal,
	SearchInput,
} from '@/components/commons'
import { GroupedList } from '@/components/grouped-list'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Button } from '@/components/ui/button'
import { adminBackRoute } from '@/constants/admin-menu'
import { ROUTES } from '@/constants/routes'
import type { KindWithRisksOutput, RiskList } from '@/types'
import { getApiErrorMessage } from '@/utils/api-error'

/* ────────────────────────── Main Page ────────────────────────────── */

export const TypologyRisksPage = () => {
	const navigate = useNavigate()
	const { data, isLoading } = useKindList()
	const { t } = useTranslation()

	const createMutation = useCreateKind()
	const updateMutation = useUpdateKind()
	const deleteMutation = useDeleteKind()

	const typologies =
		data?.items.map((k: KindWithRisksOutput) => ({
			id: k.id,
			name: k.name,
			score: k.score,
			spheraName: k.spheraName,
			children: k.risks.map((r: RiskList) => ({
				id: r.id,
				name: r.name,
			})),
		})) || []

	const [search, setSearch] = useState('')

	// Form dialog/drawer state
	const [formOpen, setFormOpen] = useState(false)
	const [editing, setEditing] = useState<Typology | undefined>(undefined)
	const [deletingTypology, setDeletingTypology] = useState<{
		id: string
		name: string
	} | null>(null)

	/* ── Filtering ── */
	const filtered = typologies.filter((typ) => {
		const q = search.toLowerCase()
		return (
			typ.name.toLowerCase().includes(q) ||
			typ.children.some((r: RiskList) => r.name.toLowerCase().includes(q))
		)
	})

	/* ── CRUD handlers ── */
	const handleOpenCreate = () => {
		setEditing(undefined)
		setFormOpen(true)
	}

	const handleOpenEdit = (typ: Typology) => {
		setEditing(typ)
		setFormOpen(true)
	}

	const handleSave = async ({
		name,
		spheraName,
		risks,
	}: {
		name: string
		spheraName: string | null
		risks: string[]
	}) => {
		try {
			if (editing) {
				const updatedRisks = risks.map((riskName, i) => ({
					id: editing.children[i]?.id,
					name: riskName,
				}))
				await updateMutation.mutateAsync({
					id: editing.id,
					data: { name, spheraName, risks: updatedRisks },
				})
				toast.success(t('administration.typologyRisks.editSuccess', { name }))
			} else {
				const updatedRisks = risks.map((riskName) => ({ name: riskName }))
				await createMutation.mutateAsync({
					name,
					spheraName,
					risks: updatedRisks,
				})
				toast.success(t('administration.typologyRisksForm.saveSuccess'))
			}
			setFormOpen(false)
			setEditing(undefined)
		} catch (error) {
			toast.error(getApiErrorMessage(error))
		}
	}

	const handleDeleteTypology = async (id: string, name: string) => {
		try {
			await deleteMutation.mutateAsync(id)
			toast.success(
				t('administration.typologyRisks.deleteTypologySuccess', { name }),
			)
		} catch (_error) {
			toast.error('Erro ao deletar')
		}
	}

	const handleConfirmDelete = async () => {
		if (!deletingTypology) {
			return
		}

		await handleDeleteTypology(deletingTypology.id, deletingTypology.name)
		setDeletingTypology(null)
	}

	/* ── Responsive form ── */
	const formTitle = editing
		? t('administration.typologyRisks.editTypology')
		: t('administration.typologyRisksForm.title')

	return (
		<>
			<AdminCardPage
				icon={<AlertTriangle className="size-5" />}
				title={t('administration.typologyRisks.title')}
				subtitle={t('administration.typologyRisks.subtitle')}
				onBack={() =>
					navigate(adminBackRoute(ROUTES.ADMINISTRATION.TYPOLOGY_RISKS))
				}
				footerText={`${filtered.length} ${
					filtered.length === 1
						? t('administration.typologyRisks.countSingular')
						: t('administration.typologyRisks.countPlural')
				}`}
				actions={
					<Button
						onClick={handleOpenCreate}
						className="gap-2 bg-kaefer-medium-grey text-white hover:bg-kaefer-medium-grey/90 w-full sm:w-auto"
					>
						<Plus className="size-4" />
						<span>{t('administration.typologyRisks.createNew')}</span>
					</Button>
				}
				modal={
					<ResponsiveFormModal
						open={formOpen}
						onOpenChange={setFormOpen}
						title={formTitle}
					>
						<TypologyFormContent
							mode={editing ? 'edit' : 'create'}
							initial={editing}
							onSave={handleSave}
							onClose={() => setFormOpen(false)}
						/>
					</ResponsiveFormModal>
				}
			>
				<SearchInput
					value={search}
					onChange={setSearch}
					placeholder={t('administration.typologyRisks.searchPlaceholder')}
				/>
				{isLoading ? (
					<div className="p-6 text-center text-sm text-muted-foreground">
						{t('administration.typologyRisks.loading')}
					</div>
				) : (
					<GroupedList
						groups={filtered}
						search={search}
						badgeClass="bg-kaefer-medium-grey/15 text-kaefer-medium-grey"
						childBadgeClass="bg-kaefer-medium-grey/15 text-kaefer-medium-grey"
						onEditGroup={handleOpenEdit}
						onDeleteGroup={(id, name) => setDeletingTypology({ id, name })}
						emptyMessage={t('administration.typologyRisks.noResults')}
						emptyIcon={
							<AlertTriangle className="size-8 text-kaefer-light-silver" />
						}
					/>
				)}
			</AdminCardPage>

			<AlertDialog
				open={!!deletingTypology}
				onOpenChange={(open) => {
					if (!open) {
						setDeletingTypology(null)
					}
				}}
			>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
						<AlertDialogDescription>
							Deseja realmente deletar a tipologia
							{deletingTypology?.name ? ` "${deletingTypology.name}"` : ''}?
						</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
						<AlertDialogAction onClick={handleConfirmDelete}>
							{t('common.delete')}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</>
	)
}
